import type {
  LeadSegment,
  BusinessKind,
  DuplicateResolution,
  ImportRowStatus,
  ImportStatus,
  AutomationDecision,
  AutomationRunStatus,
  EnrollmentStatus,
  CallOutcome,
  ConsentBasis,
  ImportConsentBasis,
  ContactRole,
  ContactVerificationStatus,
  EmailSource,
  EmailStatus,
  ReplyIntent,
  EquipmentCondition,
  EquipmentType,
  FuelType,
  MeetingOutcome,
  OperatingStatus,
  OpportunityStage,
  OwnershipType,
  PizzaFocus,
  PreferredContactMethod,
  PurchaseTimeframe,
  RecurrenceEditScope,
  RecurrenceFrequency,
  SuppressionReason,
  TaskPriority,
  TaskType,
  TicketKind,
  TicketStatus,
  WebsiteEmailSource,
  WebsiteLookupStatus,
} from './constants';

/** Human labels for fixed enums — the single source for UI text, CSV export headers and summaries. */

export const BUSINESS_KIND_LABELS: Record<BusinessKind, string> = {
  pizzeria: 'Pizzeria',
  dealer: 'Dealer',
  rep: 'Rep',
};

/** The heading of each kind's list (sidebar item, page title). */
export const BUSINESS_KIND_LIST_LABELS: Record<BusinessKind, string> = {
  pizzeria: 'Leads',
  dealer: 'Dealers',
  rep: 'Reps',
};

/** The heading of each pizzeria list (sidebar item, page title). */
export const LEAD_SEGMENT_LIST_LABELS: Record<LeadSegment, string> = {
  lead: 'Leads',
  prospect: 'Prospects',
  client: 'Clients',
  test_kitchen: 'Test Kitchens',
};

export const LEAD_SEGMENT_LABELS: Record<LeadSegment, string> = {
  lead: 'Lead',
  prospect: 'Prospect',
  client: 'Client',
  test_kitchen: 'Test Kitchen',
};

export const OPPORTUNITY_STAGE_LABELS: Record<OpportunityStage, string> = {
  qualifying: 'Qualifying',
  quote_requested: 'Quote requested',
  quote_sent: 'Quote sent',
  negotiation: 'Negotiation',
  won: 'Won',
  lost: 'Lost',
};

export const CONTACT_ROLE_LABELS: Record<ContactRole, string> = {
  owner: 'Owner',
  founder: 'Founder',
  general_manager: 'General manager',
  kitchen_manager: 'Kitchen manager',
  operations_manager: 'Operations manager',
  purchasing_manager: 'Purchasing manager',
  chef: 'Chef',
  other_decision_maker: 'Other decision maker',
  staff: 'Staff',
  other: 'Other',
};

export const CONTACT_VERIFICATION_LABELS: Record<ContactVerificationStatus, string> = {
  unverified: 'Unverified',
  verified: 'Verified',
  outdated: 'Outdated',
  invalid: 'Invalid',
};

export const PREFERRED_CONTACT_METHOD_LABELS: Record<PreferredContactMethod, string> = {
  email: 'Email',
  phone: 'Phone',
  sms: 'Text message',
  in_person: 'In person',
  any: 'No preference',
};

export const CONSENT_BASIS_LABELS: Record<ConsentBasis, string> = {
  none: 'No consent recorded',
  implied_published: 'Implied — address published',
  implied_business_relationship: 'Implied — existing business relationship',
  implied_inquiry: 'Implied — recent inquiry',
  express: 'Express consent',
  withdrawn: 'Withdrawn',
};

/** Where the addresses came from, in plain words with what CASL makes of it — asked at import and when recording consent in bulk. */
export const CONSENT_BASIS_HINTS: Record<ImportConsentBasis, string> = {
  none: 'Not sure — don’t send bulk email until consent is recorded',
  implied_published: 'Published by the business (website, listing, storefront) — implied consent',
  implied_business_relationship: 'Customers or people we did business with in the last 2 years',
  implied_inquiry: 'They asked us about something in the last 6 months',
  express: 'They agreed to receive our emails',
};

export const OWNERSHIP_TYPE_LABELS: Record<OwnershipType, string> = {
  independent: 'Independent',
  chain: 'Chain',
  franchise: 'Franchise',
  group: 'Restaurant group',
  unknown: 'Unknown',
};

export const OPERATING_STATUS_LABELS: Record<OperatingStatus, string> = {
  operating: 'Operating',
  coming_soon: 'Coming soon',
  temporarily_closed: 'Temporarily closed',
  permanently_closed: 'Permanently closed',
  unknown: 'Unknown',
};

export const PIZZA_FOCUS_LABELS: Record<PizzaFocus, string> = {
  primary: 'Pizza-focused',
  secondary: 'Pizza on the menu',
  none: 'No pizza',
  unknown: 'Unknown',
};

export const PURCHASE_TIMEFRAME_LABELS: Record<PurchaseTimeframe, string> = {
  immediate: 'Immediate',
  within_3_months: 'Within 3 months',
  within_6_months: 'Within 6 months',
  within_12_months: 'Within 12 months',
  over_12_months: 'More than 12 months',
  unknown: 'Unknown',
};

export const EQUIPMENT_TYPE_LABELS: Record<EquipmentType, string> = {
  deck_oven: 'Deck oven',
  conveyor_oven: 'Conveyor oven',
  wood_fired_oven: 'Wood-fired oven',
  gas_brick_oven: 'Gas brick oven',
  electric_oven: 'Electric pizza oven',
  rotating_oven: 'Rotating oven',
  combi_oven: 'Combi oven',
  dough_mixer: 'Dough mixer',
  dough_sheeter: 'Dough sheeter',
  dough_press: 'Dough press',
  dough_divider: 'Dough divider / rounder',
  prep_table: 'Pizza prep table',
  refrigeration: 'Refrigeration',
  proofer: 'Proofer / retarder',
  hood_ventilation: 'Hood / ventilation',
  other: 'Other equipment',
};

export const FUEL_TYPE_LABELS: Record<FuelType, string> = {
  gas: 'Gas',
  electric: 'Electric',
  wood: 'Wood',
  coal: 'Coal',
  hybrid: 'Hybrid',
  unknown: 'Unknown',
};

export const EQUIPMENT_CONDITION_LABELS: Record<EquipmentCondition, string> = {
  new: 'New',
  good: 'Good',
  fair: 'Fair',
  poor: 'Poor',
  unknown: 'Unknown',
};

export const TASK_TYPE_LABELS: Record<TaskType, string> = {
  follow_up: 'Follow-up',
  call: 'Call',
  email: 'Email',
  meeting: 'Meeting',
  reminder: 'Reminder',
  todo: 'To-do',
  note: 'Note',
};

export const RECURRENCE_FREQUENCY_LABELS: Record<RecurrenceFrequency, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  yearly: 'Yearly',
};

export const RECURRENCE_EDIT_SCOPE_LABELS: Record<RecurrenceEditScope, string> = {
  this: 'This event',
  following: 'This and following events',
  all: 'All events',
};

export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

export const CALL_OUTCOME_LABELS: Record<CallOutcome, string> = {
  connected: 'Connected',
  left_voicemail: 'Left voicemail',
  no_answer: 'No answer',
  busy: 'Busy',
  wrong_number: 'Wrong number',
};

export const MEETING_OUTCOME_LABELS: Record<MeetingOutcome, string> = {
  held: 'Held',
  scheduled: 'Scheduled',
  no_show: 'No-show',
  cancelled: 'Cancelled',
};

export const EMAIL_STATUS_LABELS: Record<EmailStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Awaiting approval',
  scheduled: 'Scheduled',
  sending: 'Sending',
  sent: 'Sent',
  delivered: 'Delivered',
  bounced: 'Bounced',
  failed: 'Failed',
  cancelled: 'Cancelled',
  uncertain: 'Delivery uncertain',
  received: 'Received',
};

export const EMAIL_SOURCE_LABELS: Record<EmailSource, string> = {
  manual: 'Manual',
  bulk: 'Bulk',
  automation: 'Automated',
  inbound: 'Inbound',
};

export const REPLY_INTENT_LABELS: Record<ReplyIntent, string> = {
  interested: 'Sounds interested',
  not_interested: 'Not interested',
  unsubscribe: 'Asked to stop',
  neutral: 'Reply',
};

export const SUPPRESSION_REASON_LABELS: Record<SuppressionReason, string> = {
  unsubscribed: 'Unsubscribed',
  hard_bounce: 'Hard bounce',
  complaint: 'Spam complaint',
  do_not_contact: 'Do not contact',
  invalid_address: 'Invalid address',
  manual: 'Added manually',
};

export const ENROLLMENT_STATUS_LABELS: Record<EnrollmentStatus, string> = {
  active: 'Active',
  paused: 'Paused',
  completed: 'Completed',
  stopped: 'Stopped',
};

export const AUTOMATION_DECISION_LABELS: Record<AutomationDecision, string> = {
  enrolled: 'Enrolled',
  email_queued: 'Email queued',
  skipped: 'Skipped',
  paused: 'Paused',
  resumed: 'Resumed',
  stopped: 'Stopped',
  completed: 'Completed',
  blocked_at_send: 'Blocked at send',
};

export const AUTOMATION_RUN_STATUS_LABELS: Record<AutomationRunStatus, string> = {
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  skipped: 'Skipped',
};

export const IMPORT_STATUS_LABELS: Record<ImportStatus, string> = {
  uploaded: 'Needs column matching',
  validating: 'Checking rows',
  validated: 'Needs review',
  importing: 'Importing',
  completed: 'Imported',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

export const IMPORT_ROW_STATUS_LABELS: Record<ImportRowStatus, string> = {
  pending: 'Not checked',
  valid: 'Ready',
  invalid: 'Has problems',
  junk: 'Junk',
  duplicate: 'Possible duplicate',
  imported: 'Created',
  merged: 'Merged',
  skipped: 'Skipped',
  failed: 'Failed',
};

export const DUPLICATE_RESOLUTION_LABELS: Record<DuplicateResolution, string> = {
  create: 'Create new lead',
  merge: 'Merge into existing',
  skip: 'Skip',
};

export const TICKET_KIND_LABELS: Record<TicketKind, string> = {
  feature: 'Feature idea',
  problem: 'Something’s wrong',
  question: 'Question',
  other: 'Other',
};

export const TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
  new: 'New',
  planned: 'Planned',
  in_progress: 'In progress',
  done: 'Done',
  declined: 'Won’t do',
};

export const WEBSITE_LOOKUP_STATUS_LABELS: Record<WebsiteLookupStatus, string> = {
  found: 'Found on the website',
  none: 'No email on the website',
  unreachable: 'Website did not answer',
  blocked: 'Website asks robots not to read it',
};

export const WEBSITE_EMAIL_SOURCE_LABELS: Record<WebsiteEmailSource, string> = {
  mailto: 'email link',
  text: 'page text',
  obfuscated: 'page text (written as “name [at] domain”)',
  cloudflare: 'protected email link',
  structured_data: 'business details in the page',
};
